import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch } from '../../hooks/redux';
import { deleteRoute, copyRoute } from '../../store/slices/routesSlice';
import { Route } from '../../types';
import { FiCalendar, FiDollarSign, FiMoreVertical, FiCopy, FiTrash2, FiArchive } from 'react-icons/fi';

interface RouteCardProps {
  route: Route;
}

const RouteCard: React.FC<RouteCardProps> = ({ route }) => {
  const dispatch = useAppDispatch();
  const [showMenu, setShowMenu] = useState(false);

  const handleDelete = async () => {
    if (window.confirm('Вы уверены, что хотите удалить этот маршрут?')) {
      await dispatch(deleteRoute(route.id));
    }
  };

  const handleCopy = async () => {
    await dispatch(copyRoute(route.id));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    const currencySymbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      RUB: '₽',
      GBP: '£',
      JPY: '¥',
    };
    
    return `${currencySymbols[currency] || currency}${amount.toLocaleString()}`;
  };

  return (
    <div className="card hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Заголовок и меню */}
        <div className="flex items-start justify-between mb-4">
          <Link 
            to={`/routes/${route.id}`}
            className="flex-1 group"
          >
            <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
              {route.title}
            </h3>
          </Link>
          
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded-lg hover:bg-gray-100"
            >
              <FiMoreVertical className="w-4 h-4 text-gray-500" />
            </button>
            
            {showMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <button
                  onClick={handleCopy}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 w-full"
                >
                  <FiCopy className="w-4 h-4" />
                  <span>Копировать</span>
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span>Удалить</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Даты */}
        <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
          <FiCalendar className="w-4 h-4" />
          <span>
            {formatDate(route.start_date)} - {formatDate(route.end_date)}
          </span>
        </div>

        {/* Стоимость */}
        {route.total_cost > 0 && (
          <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
            <FiDollarSign className="w-4 h-4" />
            <span className="font-medium text-green-600">
              {formatCurrency(route.total_cost, route.currency)}
            </span>
          </div>
        )}

        {/* Статистика */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>{route.days_count || 0} дней</span>
          <span>{route.owner}</span>
        </div>

        {/* Статус архива */}
        {route.is_archived && (
          <div className="mt-3 flex items-center space-x-2 text-sm text-gray-500">
            <FiArchive className="w-4 h-4" />
            <span>В архиве</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteCard; 